// Front-end port of the runtime Set Variable logic in
// api/src/services/workflowExecutionService.js, so the design-time autofill /
// preview computes the EXACT value the variable would hold after the node runs:
// apply the node's Mode (replace/append/merge) against the variable's current
// value, then coerce to the variable's declared type. Keep this in lockstep
// with the api functions of the same name.

function toBoolean(value) {
  if (typeof value === 'boolean') return value;
  var normalized = String(value === undefined || value === null ? '' : value).trim().toLowerCase();
  if (normalized === 'true' || normalized === '1' || normalized === 'yes') return true;
  if (normalized === 'false' || normalized === '0' || normalized === 'no') return false;
  return Boolean(value);
}

// Coerce a raw value into the variable's declared type. Returns the TYPED value
// (number / boolean / object / array / string).
function coerceValueForVariableType(dataType, rawValue) {
  var type = String(dataType || '').toLowerCase();
  if (type === 'int' || type === 'integer' || type === 'number') {
    var parsed = Number(rawValue);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (type === 'bool' || type === 'boolean') {
    return toBoolean(rawValue);
  }
  if (type === 'json' || type === 'object') {
    if (rawValue && typeof rawValue === 'object' && !Array.isArray(rawValue)) return rawValue;
    if (typeof rawValue === 'string') {
      try { return JSON.parse(rawValue); } catch (e) { return {}; }
    }
    return {};
  }
  if (type === 'array' || type === 'list') {
    if (Array.isArray(rawValue)) return rawValue;
    if (typeof rawValue === 'string') {
      try { var arr = JSON.parse(rawValue); return Array.isArray(arr) ? arr : []; } catch (e) { return []; }
    }
    return [];
  }
  // string / text — stringify objects/arrays so the variable holds readable text
  if (rawValue === undefined || rawValue === null) return '';
  if (typeof rawValue === 'string') return rawValue;
  if (typeof rawValue === 'number' || typeof rawValue === 'boolean') return String(rawValue);
  try { return JSON.stringify(rawValue); } catch (e) { return String(rawValue); }
}

// Combine the variable's current value with the new value per Mode.
function combineByMode(currentValue, newValue, mode) {
  var m = String(mode || 'replace').toLowerCase();
  if (m === 'append') {
    if (Array.isArray(currentValue)) {
      return Array.isArray(newValue) ? currentValue.concat(newValue) : currentValue.concat([newValue]);
    }
    if (typeof currentValue === 'string') {
      return currentValue + (newValue === undefined || newValue === null ? '' : String(newValue));
    }
    if (currentValue === undefined || currentValue === null || currentValue === '') {
      return Array.isArray(newValue) ? newValue.slice() : [newValue];
    }
    return newValue;
  }
  if (m === 'merge') {
    var curIsObj = currentValue && typeof currentValue === 'object' && !Array.isArray(currentValue);
    var newIsObj = newValue && typeof newValue === 'object' && !Array.isArray(newValue);
    if (curIsObj && newIsObj) return Object.assign({}, currentValue, newValue);
    if (newIsObj) return Object.assign({}, newValue);
    return newValue;
  }
  return newValue;
}

// The value the variable becomes after the Set Variable applies — mirrors the
// runtime: combine by mode (skipped for replace), then coerce to declared type.
function computeSetVariableResult(currentValue, input, mode, dataType) {
  var newValue = input;
  if (String(mode || 'replace').toLowerCase() !== 'replace') {
    newValue = combineByMode(currentValue, newValue, mode);
  }
  if (dataType) newValue = coerceValueForVariableType(dataType, newValue);
  return newValue;
}

// Serialize a typed value to the string form the Variables panel / save expects
// in paramValues.value (JSON string for object/array, plain string otherwise).
function formatValueForStorage(dataType, value) {
  var type = String(dataType || '').toLowerCase();
  if (type === 'bool' || type === 'boolean') return value ? 'true' : 'false';
  if (type === 'int' || type === 'integer' || type === 'number') {
    var numeric = Number(value);
    return Number.isFinite(numeric) ? String(numeric) : '0';
  }
  if (type === 'json' || type === 'object' || type === 'array' || type === 'list') {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    try { return JSON.stringify(value, null, 2); } catch (e) { return String(value); }
  }
  return value !== undefined && value !== null ? String(value) : '';
}

// Mirrors api/src/utils/extractOutputShape.js TRUNCATION_MARKER. The server
// clips long string leaves in the persisted node.lastOutput.shape cache and
// appends this marker. That cache is a structure preview, NOT a real value, so
// anything carrying the marker must never be baked into / previewed as a
// variable value (it would silently corrupt it). Kept as a literal here because
// the client can't import from the api package.
var SHAPE_TRUNCATION_MARKER = '…[truncated]';

// True when `value` is (or contains) a clipped structure-preview from the
// output cache, at the top level or nested inside an object/array.
function isTruncatedShapeValue(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.indexOf(SHAPE_TRUNCATION_MARKER) !== -1;
  if (typeof value === 'object') {
    try { return JSON.stringify(value).indexOf(SHAPE_TRUNCATION_MARKER) !== -1; }
    catch (e) { return false; }
  }
  return false;
}

module.exports = {
  coerceValueForVariableType: coerceValueForVariableType,
  combineByMode: combineByMode,
  computeSetVariableResult: computeSetVariableResult,
  formatValueForStorage: formatValueForStorage,
  isTruncatedShapeValue: isTruncatedShapeValue
};
